import { useCallback, useMemo, useState } from 'react';
import {
  UNDO_OFFER_MS,
  shownWidgets,
  widgetChoices,
  widgetIsRanged,
  widgetLayoutKey,
  widgetSize,
  withWidget,
  type WidgetKey,
  type WidgetReader,
  type WidgetScreen,
} from '@/domain';
import type { ReportRange } from '@/application/ports/ReportRepository';
import { useSession } from '@/application/usecases/useSession';
import { strings } from '@/shared/strings';
import { CustomizeColumns } from '@/ui/data/CustomizeColumns';
import { SettingsIcon } from '@/ui/icons';
import { toast } from '@/ui/primitives/toastStore';
import { CaseInPlace } from '@/features/orders/CaseInPlace';
import { useArrangement } from './useArrangement';
import { WidgetBoardProvider, type WidgetBoardState } from './widgetBoardContext';
import { WIDGET_COMPONENT } from './widgetRegistry';
import { BoardFailure } from './widgets/FloorWidgets';
import styles from './WidgetBoard.module.css';

const text = strings.dashboard;

const RANGES: ReadonlyArray<{ key: ReportRange; label: string }> = [
  { key: 'all', label: strings.reports.rangeAll },
  { key: 'today', label: strings.reports.rangeToday },
  { key: 'week', label: strings.reports.rangeWeek },
  { key: 'month', label: strings.reports.rangeMonth },
];

/** The widgets that read the floor's board, and so share the one sentence said when it cannot be read. */
const READS_BOARD: readonly WidgetKey[] = ['line', 'liveWork', 'attention'];

/** The widgets whose first line is not a section heading. */
const BARE: readonly WidgetKey[] = ['figures'];

export interface WidgetBoardProps {
  screen: WidgetScreen;
  /** The small word and the title at the top of the screen. */
  label: string;
  title: string;
}

/**
 * A screen made of widgets: the Dashboard, and the Overview. The lab's request
 * of 12 October 2026.
 *
 * The widgets are the ones this reader may have (`widgetChoices`), in the
 * order they arranged, or the screen as it comes for somebody who never did.
 * Customize, at the top, is the sheet every table has for its columns, said of
 * a screen: a tick shows a widget or takes it off, the handle and the arrows
 * move it, and Reset to default puts the screen back as it comes. Every change
 * is kept at once on the person's account, under the screen's own key beside
 * their tables, so it is the same on every device.
 *
 * A screen nobody arranged is drawn at once, as it always was. Somebody this
 * device has seen arrange it waits the one request for their account to say
 * how, so a person who took the production line off does not watch it appear
 * and go, and nothing is asked for on behalf of a widget that is not there
 * (`useArrangement`). If the account cannot be read, the screen comes as it
 * comes.
 *
 * The reporting range is offered while a widget that counts over one is on the
 * screen, and is this visit's: it is a question asked of the figures, not a
 * way the screen is laid out.
 *
 * A row of a list of cases opens its case in place, over the screen, so the
 * case panel is drawn here once for every widget.
 */
export function WidgetBoard({ screen, label, title }: WidgetBoardProps) {
  const { data: session } = useSession();
  // All time first and by default: the lab's request of 17 October 2026, item 4.
  const [range, setRange] = useState<ReportRange>('all');

  const views = session?.views;
  const permissions = session?.permissions;
  const reader = useMemo<WidgetReader>(
    () => ({ views: views ?? [], permissions: permissions ?? [] }),
    [views, permissions],
  );

  const control = useArrangement(widgetLayoutKey(screen));
  const { settled } = control;
  const choices = useMemo(() => widgetChoices(screen, reader), [screen, reader]);
  const shown = shownWidgets(screen, reader, control.layout);

  // The Overview reads top to bottom; the Dashboard lets widgets stand side by side.
  const stacked = screen === 'overview';
  const { layout, save, reset } = control;

  const hide = useCallback(
    (key: WidgetKey) => {
      save(withWidget(choices, layout, key, false));
      toast(key === 'notifications' ? strings.notifications.home.hidden : text.takenOff(text.widgets[key].title), {
        durationMs: UNDO_OFFER_MS,
        action: {
          label: strings.notifications.home.putBack,
          // Back to exactly what was kept before, which for most people is nothing at all.
          onClick: () => (layout === null ? reset() : save(layout)),
        },
      });
    },
    [choices, layout, save, reset],
  );

  const state = useMemo<WidgetBoardState>(
    () => ({ screen, stacked, range, hide }),
    [screen, stacked, range, hide],
  );

  return (
    <WidgetBoardProvider value={state}>
      <div>
        <div className={styles.head}>
          <span className={styles.lbl}>{label}</span>
          <h2>{title}</h2>
          <span className={styles.spacer} />
          {shown.some(widgetIsRanged) && (
            <div className={styles.seg} role="group" aria-label={strings.overview.rangeLabel}>
              {RANGES.map((option) => (
                <button
                  key={option.key}
                  className={range === option.key ? styles.on : undefined}
                  aria-pressed={range === option.key}
                  onClick={() => setRange(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
          <CustomizeColumns
            columns={choices.map((choice) => ({
              key: choice.key,
              label: text.widgets[choice.key].title,
              about: text.widgets[choice.key].about,
              optional: choice.optional,
            }))}
            customize={control}
            text={text.customize}
            icon={<SettingsIcon />}
          />
        </div>

        {settled && shown.some((key) => READS_BOARD.includes(key)) && <BoardFailure />}

        {settled &&
          (shown.length === 0 ? (
            <div className={styles.none}>
              <h3>{text.emptyHeading}</h3>
              <p>{text.emptyDetail}</p>
            </div>
          ) : (
            <div className={stacked ? styles.stacked : styles.flow}>
              {shown.map((key) => {
                const Widget = WIDGET_COMPONENT[key];
                return (
                  <div
                    key={key}
                    data-widget={key}
                    className={`${styles.widget} ${styles[widgetSize(key)]} ${BARE.includes(key) ? styles.bare : ''}`}
                  >
                    <Widget />
                  </div>
                );
              })}
            </div>
          ))}

        <CaseInPlace returnLabel={title} />
      </div>
    </WidgetBoardProvider>
  );
}
